if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });

      if (this.dataset.desktopLayout === 'thumbnail_slider') {
        this.bindDesktopThumbnailArrowScroll();
      } else if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) {
        this.removeListSemantic();
      }

      this.syncThumbnailArrowVisibility();
      window.addEventListener('resize', this.syncThumbnailArrowVisibility.bind(this));
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        this.syncThumbnailArrowVisibility();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      const isDesktopVerticalThumbnailLayout =
        this.dataset.desktopLayout === 'thumbnail_slider' &&
        window.matchMedia('(min-width: 990px)').matches;

      this.elements.thumbnails.slider.scrollTo(
        isDesktopVerticalThumbnailLayout
          ? { top: thumbnail.offsetTop }
          : { left: thumbnail.offsetLeft }
      );
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && deferredMedia.userEngaged) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    syncThumbnailArrowVisibility() {
      if (!this.elements.thumbnails) return;

      window.requestAnimationFrame(() => {
        const thumbList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        const arrowButtons = this.elements.thumbnails.querySelectorAll('button[name="previous"], button[name="next"]');
        if (!thumbList || !arrowButtons.length) return;

        const isDesktopVerticalThumbnailLayout =
          this.dataset.desktopLayout === 'thumbnail_slider' &&
          window.matchMedia('(min-width: 990px)').matches;
        const hasScrollableThumbnails = isDesktopVerticalThumbnailLayout
          ? thumbList.scrollHeight > thumbList.clientHeight + 1
          : thumbList.scrollWidth > thumbList.clientWidth + 1;

        arrowButtons.forEach((button) => {
          button.hidden = !hasScrollableThumbnails;
          button.style.display = hasScrollableThumbnails ? '' : 'none';
          button.setAttribute('aria-hidden', hasScrollableThumbnails ? 'false' : 'true');
          if (!hasScrollableThumbnails) button.removeAttribute('disabled');
          if (!hasScrollableThumbnails) button.removeAttribute('aria-disabled');
        });
      });
    }

    bindDesktopThumbnailArrowScroll() {
      if (!this.elements.thumbnails) return;

      const prevButton = this.elements.thumbnails.querySelector('button[name="previous"]');
      const nextButton = this.elements.thumbnails.querySelector('button[name="next"]');
      if (!prevButton || !nextButton) return;
      const thumbList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
      if (!thumbList) return;

      this.elements.thumbnails.enableSliderLooping = true;
      const syncArrowState = () => {
        this.syncThumbnailArrowVisibility();
        [prevButton, nextButton].forEach((button) => {
          if (button.hidden) return;
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        });
      };
      syncArrowState();

      const handleArrowClick = (event) => {
        if (!window.matchMedia('(min-width: 990px)').matches) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        syncArrowState();

        if (event.currentTarget.hidden) return;

        const thumbItems = Array.from(thumbList.querySelectorAll('[id^="Slide-Thumbnails-"]'));
        if (!thumbItems.length) return;

        const firstVisibleItem = thumbItems.find((item) => item.offsetHeight > 0) || thumbItems[0];
        if (!firstVisibleItem) return;

        const nextVisibleItem = thumbItems.find((item) => item !== firstVisibleItem && item.offsetHeight > 0);
        const step = nextVisibleItem
          ? Math.abs(nextVisibleItem.offsetTop - firstVisibleItem.offsetTop)
          : firstVisibleItem.offsetHeight;

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        thumbList.scrollBy({ top: direction * step, behavior: 'smooth' });
      };

      prevButton.addEventListener('click', handleArrowClick, true);
      nextButton.addEventListener('click', handleArrowClick, true);
      window.addEventListener('resize', syncArrowState);
    }
  });
}
